import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { getTenantId, getDepartmentId } from 'src/app/services/store-snapshot.service';
import { ALL_DEPS_TOKEN } from 'src/app/tenant-selector/state/tenant-user-access.state';
import { MainTableFilterModel } from 'src/app/shared/models/main-table-filter.model';
import { ApiResponse, ApiResponseWithLocations } from 'src/app/core/models/api-response';
import { MainTableFilteringHelperService } from 'src/app/shared/main-table-module/services/main-table-filtering-helper.service';
import { createFilterParams } from 'src/app/shared/functions/create-filter-params';
import { MainTableSearchByValueType } from 'src/app/shared/main-table-module/main-table/main-table-column-filter/main-table-column-filter.component';
import { environment } from '../../../environments/environment';
import { IDueListItem } from '../models/due-list-item.model';
import { IDueListPrintRequest } from '../models/due-list-print-request.model';
import { IDueListCounts } from '../models/due-list-counts.model';
import { mergeDueListResponsibleFilters } from '../functions/merge-due-list-responsible-filters';

const COLUMN_TO_KEY_MAP: Record<string, string> = {
  type: 'Category',
  department: 'DepartmentName',
  responsiblePositions: 'Responsibles',
  responsiblePositionGroups: 'Responsibles',
  responsibleRoles: 'Responsibles',
  responsibleUsers: 'Responsibles',
  placement: 'Location',
  expires: 'Expires',
  daysUntilExpired: 'DaysUntilExpired',
  status: 'Status',
};
const OR_GROUP_MAP: Record<string, string> = {
  responsiblePositions: 'responsible',
  responsiblePositionGroups: 'responsible',
  responsibleRoles: 'responsible',
  responsibleUsers: 'responsible',
};
@Injectable({
  providedIn: 'root',
})
export class DueListApiService {
  headers = { 'content-type': 'application/json' };
  private readonly calculateOnRead: boolean | undefined;

  private get url(): string {
    return `${environment.apiUrl}/api/${getTenantId()}/${getDepartmentId()}/dueList`;
  }
  private get dueListUrl(): string {
    return `${environment.apiUrl}/api/${getTenantId()}/dueList`;
  }

  constructor(private http: HttpClient) {
    this.calculateOnRead = environment.calculateDueListsOnRead;
  }

  getDueListWarnings(
    currentFilter?: MainTableFilterModel,
    displayRuntimeChildren?: boolean
  ): Observable<ApiResponseWithLocations<IDueListItem[]>> {
    const depId = getDepartmentId();
    if (depId === null || depId === undefined || depId === '') {
      return of();
    }
    const url = `${this.url}/warnings`;
    let params = createFilterParams(currentFilter, undefined, COLUMN_TO_KEY_MAP);
    params = MainTableFilteringHelperService.appendFilteringParams(
      params,
      mergeDueListResponsibleFilters(currentFilter, COLUMN_TO_KEY_MAP),
      COLUMN_TO_KEY_MAP,
      OR_GROUP_MAP
    );
    if (displayRuntimeChildren !== undefined) {
      params = params.set('displayRuntimeChildren', displayRuntimeChildren.toString());
    }
    if (this.calculateOnRead !== undefined) {
      params = params.set('CalculateOnRead', this.calculateOnRead.toString());
    }
    return this.http.get<ApiResponseWithLocations<IDueListItem[]>>(url, { params });
  }

  getDueListDueCompetences(
    currentFilter?: MainTableFilterModel,
    displayRuntimeChildren?: boolean
  ): Observable<ApiResponse<IDueListItem[]>> {
    const url = `${this.dueListUrl}/competences`;
    let params = createFilterParams(currentFilter);
    if (displayRuntimeChildren !== undefined) {
      params = params.set('displayRuntimeChildren', displayRuntimeChildren.toString());
    }

    return this.http.get<ApiResponse<IDueListItem[]>>(url, { params });
  }

  getMyDueListWarnings(
    currentFilter?: MainTableFilterModel,
    displayRuntimeChildren?: boolean
  ): Observable<ApiResponseWithLocations<IDueListItem[]>> {
    const depId = getDepartmentId();
    if (depId === null || depId === undefined || depId === '') {
      return of();
    }
    const url = `${this.dueListUrl}/myWarnings`;
    let params = createFilterParams(currentFilter, undefined, COLUMN_TO_KEY_MAP);
    params = MainTableFilteringHelperService.appendFilteringParams(
      params,
      mergeDueListResponsibleFilters(currentFilter, COLUMN_TO_KEY_MAP),
      COLUMN_TO_KEY_MAP,
      OR_GROUP_MAP
    );
    if (displayRuntimeChildren !== undefined) {
      params = params.set('displayRuntimeChildren', displayRuntimeChildren.toString());
    }
    if (this.calculateOnRead !== undefined) {
      params = params.set('CalculateOnRead', this.calculateOnRead.toString());
    }
    params = params.set('ExcludeArchived', 'true');
    return this.http.get<ApiResponseWithLocations<IDueListItem[]>>(url, { params });
  }

  print(request?: Partial<IDueListPrintRequest>): Observable<Blob> {
    const currentDepartmentId = getDepartmentId();
    let printUrl = '';
    if (currentDepartmentId === ALL_DEPS_TOKEN) {
      printUrl = `${this.dueListUrl}/MyWarnings/print/pdf`;
    } else {
      printUrl = `${this.url}/warnings/print/pdf`;
    }
    const filters: Record<string, Record<string, MainTableSearchByValueType>> = {};
    const orGroup: Record<string, string> = {};
    mergeDueListResponsibleFilters(request?.filter, COLUMN_TO_KEY_MAP)?.searchBy?.forEach(
      ({ property, operand, value }) => {
        const key = COLUMN_TO_KEY_MAP[property] || property;
        filters[key] ??= {};
        filters[key][operand] = value;
        if (OR_GROUP_MAP[property]) {
          orGroup[key] = OR_GROUP_MAP[property];
        }
      }
    );
    const searchBy = Object.fromEntries(
      Object.entries(filters).map(([key, value]) => [key, JSON.stringify(value)])
    );
    const orderBy: Record<string, string> = {};
    if (request?.filter?.orderBy) {
      orderBy[request.filter.orderBy] = request.filter.ascDesc || 'Descending';
    }

    const body = {
      recordsToPrint: request?.recordsToPrint || [],
      categoriesToPrint: request?.categoriesToPrint || [],
      displayRuntimeChildren: request?.displayRuntimeChildren ?? true,
      pageSize: request?.filter?.pageSize,
      pageIndex: request?.filter?.pageIndex,
      globalSearch: request?.filter?.searchTerm || '',
      searchBy,
      orGroup,
      orderBy,
      CalculateOnRead: this.calculateOnRead ?? true,
    };

    return this.http.post(printUrl, body, { responseType: 'blob' });
  }

  getDueListCounts(): Observable<IDueListCounts> {
    let { url } = this;
    const depId = getDepartmentId();
    let params = new HttpParams();
    if (depId === ALL_DEPS_TOKEN) {
      url = this.dueListUrl;
      params = params.set('ExcludeArchived', 'true');
    }
    url = `${url}/all/counts`;
    if (this.calculateOnRead !== undefined) {
      params = params.set('CalculateOnRead', this.calculateOnRead.toString());
    }
    return this.http.get<IDueListCounts>(url, { params });
  }
}
